import { AlertTriangle, Download, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useOrganizationWalletAllocation } from "../hooks/useOrganizationWalletAllocation";
import { useOrganizationWalletBalance } from "../hooks/useOrganizationWalletBalance";
import { usePocketsVisibility } from "../hooks/usePocketsVisibility";

const formatUSD = (raw: string | number) => {
  const value = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Container: wires the wallet balance / allocation hooks and navigation to the presentational view. Keep this
 * thin — all rendering lives in WalletBalanceBannerView so it can be tested in isolation (see
 * WalletBalanceBanner.stories.tsx).
 */
export const WalletBalanceBanner = () => {
  const navigate = useNavigate();

  const { balance, isLoading, error: rawError } = useOrganizationWalletBalance();
  const { aggregates, isLoading: isLoadingAllocation } = useOrganizationWalletAllocation();
  const { visible: pocketsVisible } = usePocketsVisibility();

  return (
    <WalletBalanceBannerView
      currentBalance={balance ? Number(balance.current.balance) : 0}
      pocketsTotal={aggregates?.pockets_total ?? 0}
      cardsLoadedTotal={aggregates?.cards_loaded_total ?? 0}
      showPockets={pocketsVisible}
      isLoading={isLoading || isLoadingAllocation}
      error={rawError ?? null}
      onMovements={() => navigate("/app/cards/movements")}
    />
  );
};

export interface WalletBalanceBannerViewProps {
  currentBalance: number;
  pocketsTotal: number;
  cardsLoadedTotal: number;
  /** Hide the pockets allocation card when the org is not using pockets. Defaults to shown. */
  showPockets?: boolean;
  isLoading: boolean;
  error: string | null;
  onMovements: () => void;
}

/** Presentational banner — pure function of its props (no hooks beyond i18n, no data fetching). */
export const WalletBalanceBannerView = ({
  currentBalance,
  pocketsTotal,
  cardsLoadedTotal,
  showPockets = true,
  isLoading,
  error,
  onMovements,
}: WalletBalanceBannerViewProps) => {
  const { t } = useTranslations("cards");

  const totalBalance = currentBalance + (showPockets ? pocketsTotal : 0) + cardsLoadedTotal;
  const parts: AllocationPart[] = [
    {
      key: "available",
      label: t("wallet_banner_allocation_available"),
      description: t("wallet_banner_allocation_available_description"),
      value: currentBalance,
      accent: "rgba(255,255,255,0.95)",
    },
    ...(showPockets
      ? [
          {
            key: "pockets",
            label: t("wallet_banner_allocation_pockets"),
            description: t("wallet_banner_allocation_pockets_description"),
            value: pocketsTotal,
            accent: "#7dd3a4",
          },
        ]
      : []),
    {
      key: "cards",
      label: t("wallet_banner_allocation_cards"),
      description: t("wallet_banner_allocation_cards_description"),
      value: cardsLoadedTotal,
      accent: "#bef264",
    },
  ];

  return (
    // Laid out against its own width (`@container`). Narrow: the balance, what the wallet is for, where the
    // money sits, then "Movimientos" at full width. Wide: "Movimientos" moves up beside the balance and the
    // breakdown spreads into columns. The breakdown used to be one boxed card per part, three stacked boxes
    // on a phone; one bar says the same at a glance and the legend under it carries the figures.
    <section
      className={cn(
        "@container relative overflow-hidden rounded-2xl shadow-sm",
        "bg-[linear-gradient(120deg,#003D31_0%,#00735C_55%,#00A380_100%)]",
        "dark:bg-[linear-gradient(120deg,#002820_0%,#00523F_55%,#007A5F_100%)]",
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)",
          backgroundSize: "8px 8px",
        }}
      />

      <div className="relative p-5 text-white @3xl:p-8">
        {error ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            heading={t("wallet_banner_error_load")}
            text={error}
            className="bg-white/10 text-white border-white/30 [&_[data-slot=alert-description]]:text-white/80"
          />
        ) : (
          <div className="grid gap-5 @3xl:grid-cols-[minmax(0,1fr)_auto] @3xl:gap-x-8 @3xl:gap-y-6">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-white/70">
                <Wallet className="h-4 w-4" aria-hidden="true" />
                <span className="text-[11px] font-mono uppercase tracking-wider">
                  {t("wallet_banner_label")}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-100">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  {t("wallet_banner_status_active")}
                </span>
              </div>

              {/* Wraps rather than overflows: a seven-figure balance in this size outgrows a phone's width. */}
              <div className="mt-3 flex flex-wrap items-baseline gap-x-2">
                {isLoading ? (
                  <ColocaSkeleton className="h-10 w-48 bg-white/20" />
                ) : (
                  <>
                    <span className="text-3xl font-semibold font-mono tabular-nums tracking-tight text-white @md:text-4xl">
                      {formatUSD(totalBalance)}
                    </span>
                    <span className="text-base font-medium text-white/70">USD</span>
                  </>
                )}
              </div>

              <p className="mt-2 max-w-xl text-sm text-white/75">{t("wallet_banner_description")}</p>
            </div>

            <div className="@3xl:col-span-2">
              <AllocationBar parts={parts} total={totalBalance} isLoading={isLoading} />
              <ul
                className={cn(
                  "mt-4 grid divide-y divide-white/10 @3xl:divide-x @3xl:divide-y-0",
                  showPockets ? "@3xl:grid-cols-3" : "@3xl:grid-cols-2",
                )}
              >
                {parts.map((part) => (
                  <AllocationRow key={part.key} part={part} isLoading={isLoading} />
                ))}
              </ul>
            </div>

            <div className="@3xl:col-start-2 @3xl:row-start-1">
              <ColocaButton
                variant="ghost"
                icon={Download}
                onClick={onMovements}
                className="w-full bg-white/10 text-white ring-1 ring-white/20 hover:bg-white/20 hover:text-white @3xl:w-auto dark:hover:bg-white/20"
              >
                {t("wallet_banner_btn_movements")}
              </ColocaButton>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

interface AllocationPart {
  key: string;
  label: string;
  description: string;
  value: number;
  accent: string;
}

/**
 * Where the balance sits, as one strip. Decorative on purpose (`aria-hidden`) and not a `ColocaProgress`:
 * this is a split of one total, not progress towards anything, and the legend under it reads every figure as
 * text.
 */
const AllocationBar = ({
  parts,
  total,
  isLoading,
}: {
  parts: AllocationPart[];
  total: number;
  isLoading: boolean;
}) => (
  <div aria-hidden="true" className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-white/15">
    {!isLoading &&
      total > 0 &&
      parts
        .filter((part) => part.value > 0)
        .map((part) => (
          <span
            key={part.key}
            className="h-full min-w-1 transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: `${(part.value / total) * 100}%`, background: part.accent }}
          />
        ))}
  </div>
);

/** Narrow: label and description on the left, the figure on the right. Wide: one column per part. */
const AllocationRow = ({ part, isLoading }: { part: AllocationPart; isLoading: boolean }) => (
  <li className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 py-3 first:pt-0 last:pb-0 @3xl:grid-cols-1 @3xl:px-4 @3xl:py-0 @3xl:first:pl-0 @3xl:last:pr-0">
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-white/65">
      <span className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: part.accent }} />
      <span>{part.label}</span>
    </div>
    {isLoading ? (
      <ColocaSkeleton className="h-5 w-20 bg-white/20 @3xl:mt-1 @3xl:h-6 @3xl:w-24" />
    ) : (
      <p className="text-base font-semibold font-mono tabular-nums text-white @3xl:mt-1 @3xl:text-xl">
        {formatUSD(part.value)}
      </p>
    )}
    <p className="mt-0.5 text-[11px] text-white/55">{part.description}</p>
  </li>
);
